/**
 * Compliance API module - frameworks, controls, evidence, audit, drift,
 * scoring, attestations, exceptions, gap analysis, workflows, FedRAMP,
 * agentless scanning, rules engine, etc.
 *
 * Real per-domain split: contains the actual implementation of the
 * complianceApi object, extracted verbatim from the monolithic
 * frontend/src/services/api.ts as part of the ADR-0003 follow-up.
 *
 * Note: The compliance hub service is canonical at /api/v1/compliance/*
 * (per ADR-0001). This module is the frontend client for that service.
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import type { ComplianceFramework } from '../../types';
import { asArray } from '../../utils/asArray';

// -- complianceApi --
export const complianceApi = {
  // Frameworks
  // R147 — optional `enabled` filter wired through to BE; pre-R147 the call
  // returned all frameworks regardless of per-tenant enable toggle.
  getFrameworks: async (params?: { enabled?: boolean }): Promise<ComplianceFramework[]> => {
    const qs = params && params.enabled !== undefined ? `?enabled=${params.enabled}` : '';
    const data: any = _unwrap(await fetchClient.request<any>(`/api/v1/compliance/frameworks${qs}`));
    return asArray(data, 'frameworks');
  },

  getOverview: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/stats'));
  },

  getFrameworkDetails: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/frameworks/${id}`));
  },

  runAssessment: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/assessment/${frameworkId}`));
  },

  // Controls
  getAllControls: async (params?: { framework?: string; status?: string; severity?: string; search?: string }): Promise<any> => {
    const qs = new URLSearchParams();
    if (params?.framework) qs.append('framework', params.framework);
    if (params?.status) qs.append('status', params.status);
    if (params?.severity) qs.append('severity', params.severity);
    if (params?.search) qs.append('search', params.search);
    const q = qs.toString();
    const data: any = _unwrap(await fetchClient.request<any>(`/api/v1/compliance/controls${q ? `?${q}` : ''}`));
    return asArray(data, 'controls');
  },

  getControlDetail: async (controlId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/controls/${controlId}`));
  },

  // Stats
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/stats'));
  },

  // Evidence
  getEvidence: async (params?: { framework?: string; control_id?: string }): Promise<any> => {
    const qs = new URLSearchParams();
    if (params?.framework) qs.append('framework', params.framework);
    if (params?.control_id) qs.append('control_id', params.control_id);
    const q = qs.toString();
    const data: any = _unwrap(await fetchClient.request<any>(`/api/v1/compliance/evidence${q ? `?${q}` : ''}`));
    return asArray(data, 'evidence');
  },

  uploadEvidence: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/evidence', data));
  },

  // Audit Trail
  getAuditTrail: async (params?: { limit?: number }): Promise<any> => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.append('limit', String(params.limit));
    const q = qs.toString();
    const data: any = _unwrap(await fetchClient.request<any>(`/api/v1/compliance/audit-trail${q ? `?${q}` : ''}`));
    return asArray(data, 'entries');
  },

  // Gap Analysis
  getGapAnalyses: async (): Promise<any> => {
    const data: any = _unwrap(await fetchClient.request<any>('/api/v1/compliance/gap-analysis'));
    return asArray(data, 'gap_analyses');
  },

  getGapAnalysis: async (framework: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/gap-analysis/${framework}`));
  },

  // Reports
  getReports: async (): Promise<any> => {
    const data: any = _unwrap(await fetchClient.request<any>('/api/v1/compliance/reports/list'));
    return asArray(data, 'reports');
  },

  generateReport: async (data: { framework: string; report_type?: string; format?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/reports/generate', data));
  },

  generateAuditReport: async (data: { framework: string; format?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/reports/audit-ready', data));
  },

  downloadReport: async (reportId: string): Promise<Blob> => {
    const response = await fetchClient.download(`/api/v1/compliance/reports/${reportId}/download`);
    if (response.error || !response.data) {
      throw new Error(response.error || 'Download failed');
    }
    return response.data;
  },

  // Auditor-ready per-framework export as raw bytes (PDF via reportlab, or CSV).
  // BE routes: GET /api/v1/compliance/reports/{framework}/export.{pdf,csv}
  // (reports.py:478 / :614). Returns the real file bytes as a Blob — the caller
  // saves it directly instead of JSON.stringify()-ing a Blob.
  downloadReportExport: async (framework: string, format: 'pdf' | 'csv' = 'pdf'): Promise<Blob> => {
    const ext = format === 'csv' ? 'csv' : 'pdf';
    const response = await fetchClient.download(`/api/v1/compliance/reports/${encodeURIComponent(framework)}/export.${ext}`);
    if (response.error || !response.data) {
      throw new Error(response.error || 'Download failed');
    }
    return response.data;
  },

  // Attestations
  getAttestations: async (params?: { status?: string }): Promise<any> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    const q = qs.toString();
    const data: any = _unwrap(await fetchClient.request<any>(`/api/v1/compliance/attestations${q ? `?${q}` : ''}`));
    return asArray(data, 'attestations');
  },

  createAttestation: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/attestations', data));
  },

  approveAttestation: async (attestationId: string, notes?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/attestations/${attestationId}/approve`, { notes }));
  },

  rejectAttestation: async (attestationId: string, notes: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/attestations/${attestationId}/reject`, { notes }));
  },

  // Drift Detection
  getDriftEvents: async (params?: { framework?: string; severity?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/drift', params as any));
  },

  resolveDrift: async (driftId: string, notes?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/drift/${driftId}/resolve`, { notes }));
  },

  // Trends
  getTrends: async (params?: { weeks?: number; framework?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/trends', params as any));
  },

  // Real compliance-score trend for a single framework, sourced from
  // csp_compliance.score_history (populated every COMPLIANCE_TREND_INTERVAL_SECONDS).
  getFrameworkScoreTrend: async (frameworkId: string, days: number = 14): Promise<{
    framework_id: string;
    framework_name?: string | null;
    window_days: number;
    points: Array<{ day: string; score: number; passing: number; failing: number; total: number }>;
    current_score: number;
    delta_30d: number;
    source?: string;
  }> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/trend', { framework_id: frameworkId, days }));
  },

  // Every framework's score trend in ONE request. Fetching them individually
  // meant one GET per framework — 153 on a tenant with the full catalogue —
  // which saturated the connection pool and mostly ended as aborted requests.
  getAllFrameworkScoreTrends: async (days: number = 14): Promise<{
    window_days: number;
    frameworks: Array<{
      framework_id: string;
      framework_name?: string | null;
      current_score: number;
      points: Array<{ day: string; score: number; passing: number; failing: number; total: number }>;
    }>;
    source?: string;
  }> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/trend', { with_points: true, days }));
  },

  // Exceptions
  getExceptions: async (params?: { status?: string; framework?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/exceptions', params as any));
  },

  createException: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/exceptions', data));
  },

  approveException: async (exceptionId: string, notes?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/exceptions/${exceptionId}/approve`, { notes }));
  },

  rejectException: async (exceptionId: string, reason?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/exceptions/${exceptionId}/reject`, { reason }));
  },

  getComplianceIssues: async (params?: { status?: string; severity?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/issues', params as any));
  },

  // R201 — Wiz parity: compliance bridge (DSPM + CIEM + SaaS correlation).
  // BE endpoints existed in compliance-hub/routers/bridge.py but no FE caller.
  // These are read endpoints — full bulk-import wizard would be a separate ship.
  getBridgeSaaSSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/bridge/saas-summary'));
  },
  getBridgeSaaSRisks: async (params?: { severity?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/bridge/saas-risks', params as any));
  },
  getBridgeRisks: async (params?: { severity?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/bridge/risks', params as any));
  },
  getBridgeComplianceViolations: async (params?: { framework_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/bridge/compliance-violations', params as any));
  },
  importSaaSStore: async (data: { app_name: string; tenant_id?: string; metadata?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/bridge/saas-import', data));
  },
  bulkImportSaaSStores: async (data: { stores: any[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/bridge/saas-import-bulk', data));
  },
  runBridgeCorrelation: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/bridge/correlate', {}));
  },

  getFrameworkVersions: async (frameworkId?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/framework-versions', frameworkId ? { framework_id: frameworkId } : undefined));
  },

  upgradeFrameworkVersion: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/framework-versions/${frameworkId}/upgrade`));
  },

  // Control Mappings
  getControlMappings: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/control-mappings'));
  },

  getControlMappingDetail: async (unifiedId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/control-mappings/${unifiedId}`));
  },

  // Regulatory Changes
  getRegulatoryChanges: async (params?: { framework?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/regulatory-changes', params as any));
  },

  // Cross-Module
  getCrossModuleCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/cross-module'));
  },

  syncCrossModule: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/cross-module/sync'));
  },

  // Custom Frameworks
  createCustomFramework: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/frameworks/custom', data));
  },

  // Executive Summary
  getExecutiveSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/executive-summary'));
  },

  // Convenience alias for compliance trends with weeks-only param
  complianceTrends: async (weeks?: number): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/trends', weeks ? { weeks } : undefined));
  },

  // Assessment Schedules
  getAssessmentSchedules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/assessment-schedules'));
  },

  createAssessmentSchedule: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/assessment-schedules', data));
  },

  updateAssessmentSchedule: async (scheduleId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/compliance/assessment-schedules/${scheduleId}`, data));
  },

  deleteAssessmentSchedule: async (scheduleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/compliance/assessment-schedules/${scheduleId}`));
  },

  triggerAssessment: async (scheduleId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/assessment-schedules/${scheduleId}/trigger`));
  },

  getAssessmentHistory: async (params?: { framework?: string; schedule_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/assessment-history', params as any));
  },

  // Score & Breakdown
  getComplianceScore: async (params?: { framework?: string; algorithm?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/score', params as any));
  },

  getScoreBreakdown: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/score/breakdown'));
  },

  // Evidence Collection
  collectEvidence: async (data: { control_id: string; framework?: string; account_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/evidence/collect', data));
  },

  collectEvidenceBulk: async (data: { framework: string; account_id?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/evidence/bulk-collect', data));
  },

  exportEvidence: async (data: { framework: string; include_remediation?: boolean; include_evidence?: boolean; include_trends?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/evidence/export', data));
  },

  getFrameworkEvidence: async (framework: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/evidence/${framework}`));
  },

  getControlEvidence: async (framework: string, controlId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/evidence/${framework}/controls/${controlId}`));
  },

  // Compliance Export
  exportComplianceData: async (framework: string, format?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/export/${framework}`, format ? { format } : undefined));
  },

  // Dashboard Widgets
  getWidgets: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/widgets'));
  },

  // Gap Analysis Compare
  compareGapAnalysis: async (data: { framework: string; target_score?: number; target_date?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/gap-analyses/compare', data));
  },

  // Control Mappings Discovery
  discoverControlMappings: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/control-mappings/discover'));
  },

  // Finding-to-Control Auto-Mapping (CSPM/CWPP/CIEM → Compliance Controls)
  autoMapFindings: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/findings/auto-map'));
  },

  // Trend Snapshots
  createTrendSnapshot: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/trends/snapshot'));
  },

  // Applicability Assessments
  getApplicabilityAssessments: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/applicability'));
  },

  getApplicabilityAssessment: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/applicability/${frameworkId}`));
  },

  createApplicabilityAssessment: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/applicability', data));
  },

  // Control Inheritance
  getControlInheritance: async (params?: { framework?: string; inheritance_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/control-inheritance', params as any));
  },

  getControlInheritanceDetail: async (inheritanceId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/control-inheritance/${inheritanceId}`));
  },

  createControlInheritance: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/control-inheritance', data));
  },

  // Historical Snapshots
  getHistoricalSnapshots: async (params?: { limit?: number; snapshot_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/snapshots', params as any));
  },

  getHistoricalSnapshot: async (snapshotId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/snapshots/${snapshotId}`));
  },

  createSnapshot: async (data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/snapshots', data || {}));
  },

  compareSnapshots: async (snapshotA: string, snapshotB: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/snapshots/compare', { snapshot_a: snapshotA, snapshot_b: snapshotB }));
  },

  // Auditor Workspace
  getAuditorSessions: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/auditor-workspace'));
  },

  getAuditorSession: async (sessionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/auditor-workspace/${sessionId}`));
  },

  createAuditorSession: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/auditor-workspace', data));
  },

  revokeAuditorSession: async (sessionId: string, reason?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/auditor-workspace/${sessionId}/revoke`, { reason }));
  },

  getAuditorView: async (sessionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/auditor-workspace/${sessionId}/view`));
  },

  addAuditorComment: async (sessionId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/auditor-workspace/${sessionId}/comment`, data));
  },

  // Catalog
  getCatalogFrameworks: async (params?: { category?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/catalog/frameworks', params as any));
  },

  getCatalogFrameworkDetail: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/catalog/frameworks/${frameworkId}`));
  },

  // Heatmap
  getComplianceHeatmap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/heatmap'));
  },

  // Framework Mappings
  getFrameworkMappings: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/frameworks/${frameworkId}/mappings`));
  },

  // Scoring
  getFrameworkScoring: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/scoring/${frameworkId}`));
  },

  // Policy Evaluation (OPA/Rego)
  evaluatePolicy: async (data: { policy_id?: string; policy_name?: string; rego_code?: string; input_data?: any; framework?: string; severity?: string; type?: string; scope?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/policies/evaluate', data));
  },

  // Audit Package
  getAuditPackage: async (framework: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/${framework}/audit-package`));
  },

  // Report Templates
  getReportTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/reports/templates'));
  },

  generateReportFromTemplate: async (data: { template_id: string; format?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/reports/from-template', data));
  },

  // Bulk Control Assessment
  bulkAssessControls: async (data: { framework?: string; category?: string; control_ids?: string[]; action?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/controls/bulk-assess', data));
  },

  // Cross-Module Ingestion
  ingestModuleData: async (data: { module: string; data: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/cross-module/ingest', data));
  },

  // Audit Trail Search & Export
  searchAuditTrail: async (params?: { action?: string; user?: string; resource_type?: string; start_date?: string; end_date?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/audit-trail/search', params as any));
  },

  exportAuditTrail: async (params?: { format?: string; start_date?: string; end_date?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/audit-trail/export', params as any));
  },

  // Framework Trends
  getFrameworkTrend: async (frameworkId: string, days?: number): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/trends/${frameworkId}`, days ? { days } : undefined));
  },

  // --- Continuous Assessment Scheduler ---
  getContinuousAssessmentStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/continuous-assessment/status'));
  },
  triggerContinuousAssessment: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/continuous-assessment/run-now'));
  },
  updateContinuousAssessmentConfig: async (data: { interval_seconds: number }): Promise<any> => {
    return _unwrap(await fetchClient.put<any>('/api/v1/compliance/continuous-assessment/config', data));
  },

  // --- Agentless Compliance Scanning ---
  startAgentlessScan: async (data: { provider: string; account_id?: string; regions?: string[]; frameworks?: string[]; scan_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/agentless/scan', data));
  },
  getAgentlessScans: async (params?: { provider?: string; status?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/agentless/scans', params as any));
  },
  getAgentlessScan: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/agentless/scans/${scanId}`));
  },
  cancelAgentlessScan: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/agentless/scans/${scanId}/cancel`));
  },
  getAgentlessSupportedProviders: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/agentless/supported-providers'));
  },
  getAgentlessScanConfigs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/agentless/configs'));
  },
  createAgentlessScanConfig: async (data: { name: string; provider: string; account_id?: string; regions?: string[]; frameworks?: string[]; schedule?: string; enabled?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/agentless/configs', data));
  },
  updateAgentlessScanConfig: async (configId: string, data: Record<string, any>): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/compliance/agentless/configs/${configId}`, data));
  },
  deleteAgentlessScanConfig: async (configId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/compliance/agentless/configs/${configId}`));
  },
  getAgentlessResourceInventory: async (scanId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/agentless/resource-inventory/${scanId}`));
  },

  // --- Compliance Rules Engine ---
  getComplianceRules: async (params?: { provider?: string; framework?: string; severity?: string; search?: string; limit?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/rules', params as any));
  },
  getComplianceRulesSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/rules/summary'));
  },
  getComplianceRule: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/rules/${ruleId}`));
  },
  getRuleRemediation: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/rules/${ruleId}/remediation`));
  },
  evaluateComplianceRule: async (data: { rule_id: string; resource_data: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/rules/evaluate', data));
  },
  getRuleMappings: async (params?: { framework?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/rule-mappings', params as any));
  },

  // --- Compliance Heatmap (cross-framework x cross-account) ---
  getHeatmap: async (params?: { frameworks?: string; accounts?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/heatmap', params as any));
  },
  getHeatmapDrilldown: async (frameworkId: string, accountId?: string): Promise<any> => {
    // R145: framework ids are display names and one of them is
    // "BaFin BAIT/VAIT". Interpolating that into a path segment split the URL
    // and the drill-in 404'd; percent-encoding does not help because the path
    // is decoded before routing (%2F 404s identically — verified live). Send it
    // as a slash-safe QUERY parameter instead. Any id, any punctuation.
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/heatmap', {
      fw: frameworkId,
      ...(accountId ? { account_id: accountId } : {}),
    } as any));
  },

  // --- Compliance Posture Overview ---
  getCompliancePosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/posture'));
  },

  // --- Compliance Workflows ---
  getWorkflows: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/workflows'));
  },
  getWorkflowTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/workflows/templates'));
  },
  createWorkflow: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/workflows', data));
  },
  executeWorkflow: async (workflowId: string, triggerEvent?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/workflows/${workflowId}/execute`, { trigger_event: triggerEvent }));
  },
  getWorkflowExecutions: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/workflows/executions', params as any));
  },
  getWorkflowExecution: async (executionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/workflows/executions/${executionId}`));
  },
  approveWorkflowStep: async (executionId: string, stepId: string, approver?: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/compliance/workflows/executions/${executionId}/approve/${stepId}`, { approver }));
  },

  // --- Response Actions Catalog ---
  getResponseActions: async (params?: { provider?: string; category?: string; disruptive_only?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/response-actions', params as any));
  },

  // --- Audit Boards ---
  getAuditBoards: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/audit-boards'));
  },
  getAuditBoardTemplates: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/audit-boards/templates'));
  },
  createAuditBoard: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/audit-boards', data));
  },
  getAuditBoard: async (boardId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/compliance/audit-boards/${boardId}`));
  },
  updateAuditBoard: async (boardId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/compliance/audit-boards/${boardId}`, data));
  },

  // --- FedRAMP Extended ---
  getFedRAMPStatus: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/fedramp/status'));
  },
  getFedRAMPControls: async (params?: { family?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/fedramp/controls', params as any));
  },
  getFedRAMPPOAMs: async (params?: { status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/fedramp/poams', params as any));
  },
  createFedRAMPPOAM: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/fedramp/poams', data));
  },
  updateFedRAMPPOAM: async (poamId: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/compliance/fedramp/poams/${poamId}`, data));
  },
  getFedRAMPContinuousMonitoring: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/fedramp/continuous-monitoring'));
  },

  // --- Drift Detection Extended ---
  getDriftSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/drift/summary'));
  },

  // --- Event-Driven Hooks ---
  notifyResourceCreated: async (data: { resource: any; provider: string; resource_type: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/events/resource-created', data));
  },
  notifyConfigurationChanged: async (data: { resource_id: string; provider: string; resource_type: string; old_config: any; new_config: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/compliance/events/configuration-changed', data));
  },

  // --- Benchmarking Extended ---
  getBenchmarking: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/benchmarking'));
  },
  getBenchmarkPercentile: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/benchmarking/percentile'));
  },
  getBenchmarkTrends: async (params?: { framework?: string; months?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/compliance/benchmarking/trends', params as any));
  },
};
